/* eslint-disable no-undef */
/* eslint-disable no-alert */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import RequesterModal from '../../components/RequesterModal';

export default function PreticketsAdd() {
  const userData = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const { state } = useLocation();
  const notification = useNotification(); // Hook chamado no topo do componente
  const {
    email,
    title: preticketTitle,
    message: preticketMessage,
    files: preticketFiles,
    id: preticketId, // Adicionando o ID do pré-ticket para exclusão posterior
  } = state || {};

  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para controle do loading
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedRequesterId, setSelectedRequesterId] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(userData.empresa_id);
  // Cada entrada: { requesterId, companyId, requesterName, companyName }
  const [allRequesterOptions, setAllRequesterOptions] = useState([]);
  const [showRequesterModal, setShowRequesterModal] = useState(false);
  const [requesterNotFound, setRequesterNotFound] = useState(false);
  // Processa a mensagem do preticket removendo tags HTML e formatando adequadamente
  const processPreticketMessage = (message) => {
    if (!message) return '';
    return message
      .replace(/<[^>]*>/g, '') // Remove todas as tags HTML
      .replace(/&nbsp;/g, ' ') // Substitui &nbsp; por espaços
      .replace(/&lt;/g, '<') // Decodifica entidades HTML
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim(); // Remove espaços extras
  };

  // Transforma o HTML original do preticket para um formato seguro e legível
  const transformPreticketHtml = (html) => {
    const src = html || '';
    return src
      .replace(/<p><br><\/p>/g, '<br>')
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '<br>')
      .replace(/(<br>\s*){2,}/g, '<br><br>')
      .replace(/^<br>|<br>$/g, '')
      .replace(
        /<img([^>]*?)style="([^"]*?)"([^>]*?)>/g,
        '<img$1style="$2; max-width: 100%; height: auto;"$3>'
      )
      .replace(
        /<img(?![^>]*style)([^>]*?)>/g,
        '<img$1 style="max-width: 100%; height: auto;">'
      );
  };

  // Injeta token nas URLs /api/download/ dentro de atributos src do HTML
  const injectTokenInHtml = (html) => {
    if (!html || !authToken) return html;
    return html.replace(
      /(src\s*=\s*)(["'])(\/api\/download\/[^"'?]+)([^"']*\2)/g,
      (_match, prefix, quote, url, rest) =>
        `${prefix}${quote}${url}?token=${authToken}${rest}`
    );
  };

  const [clientCommunication, setClientCommunication] = useState(
    preticketMessage || ''
  );
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesk, setSelectedServiceDesk] = useState('');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [title, setTitle] = useState(`Email: ${preticketTitle || ''}`);
  const [errors] = useState({});
  const navigate = useNavigate();
  const [requesterDetails, setRequesterDetails] = useState(null);

  const handleOpenRequesterModal = () => {
    setShowRequesterModal(true);
  };

  const handleCloseRequesterModal = () => {
    setShowRequesterModal(false);
  };

  const handleRequesterCreated = async (newRequester) => {
    setSelectedRequesterId(newRequester.id);
    setSelectedCompany(newRequester.id_customers);
    setRequesterNotFound(false);
    setShowRequesterModal(false);

    try {
      const serviceDeskResponse = await axios.get(
        `/servicedesks/customer/${newRequester.id_customers}`
      );
      setServiceDesks(serviceDeskResponse.data);

      if (serviceDeskResponse.data.length > 0) {
        const {
          departments: deptOptions,
          priorities: prioOptions,
          stages: stageOpts,
        } = serviceDeskResponse.data[0];
        setDepartmentOptions(deptOptions);
        setPriorityOptions(prioOptions);
        setStageOptions(stageOpts);
      }
    } catch (error) {
      console.error('Erro ao carregar service desks:', error);
    }

    notification('Solicitante criado com sucesso!', 'success');
  };

  useEffect(() => {
    let parsedFiles = [];
    if (preticketFiles) {
      try {
        // Verificar se preticketFiles é uma string JSON válida antes de fazer o parse
        parsedFiles =
          typeof preticketFiles === 'string'
            ? JSON.parse(preticketFiles)
            : preticketFiles;
        if (!Array.isArray(parsedFiles)) {
          parsedFiles = []; // Se o resultado não for um array, defina-o como array vazio
        }
      } catch (error) {
        console.error('Erro ao fazer o parse de preticketFiles:', error);
        parsedFiles = []; // Em caso de erro, assegure que parsedFiles seja um array vazio
      }
    }

    const loadRequesterInfo = async () => {
      try {
        if (!email) {
          console.error('Email do solicitante não encontrado.');
          navigate('/pretickets');
          return;
        }

        const response = await axios.get(`/requester-info/${email}`);
        const rows = response.data || [];
        if (!rows.length) {
          setRequesterNotFound(true);
          setDataLoaded(true);
          return;
        }

        // Busca nome de cada solicitante e nome de cada empresa em paralelo
        const uniqueCompanyIds = Array.from(
          new Set(rows.map((r) => r.id_customers))
        );
        const [requesterResults, companyResults] = await Promise.all([
          Promise.allSettled(
            rows.map((r) => axios.get(`/requesters/${r.id_requesters}`))
          ),
          Promise.allSettled(
            uniqueCompanyIds.map((cid) => axios.get(`/customers/${cid}`))
          ),
        ]);

        // Monta mapa companyId → nome da empresa
        const companyNameMap = {};
        companyResults.forEach((res, idx) => {
          if (res.status === 'fulfilled') {
            const c = res.value.data;
            companyNameMap[uniqueCompanyIds[idx]] =
              c.nomeEmpresa || c.razaoSocial || String(uniqueCompanyIds[idx]);
          }
        });

        // Monta lista completa de opções: um item por solicitante
        const options = rows.map((r, idx) => {
          const rData =
            requesterResults[idx].status === 'fulfilled'
              ? requesterResults[idx].value.data
              : null;
          const requesterName = rData
            ? rData.name || rData.email || email
            : email;
          return {
            requesterId: r.id_requesters,
            companyId: r.id_customers,
            requesterName,
            companyName:
              companyNameMap[r.id_customers] || String(r.id_customers),
          };
        });
        setAllRequesterOptions(options);

        // Seleciona o primeiro da lista como padrão.
        // O useEffect de selectedCompany carregará as mesas automaticamente.
        const first = options[0];
        if (first) {
          setSelectedRequesterId(first.requesterId);
          setSelectedCompany(first.companyId);
        }

        setDataLoaded(true);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setRequesterNotFound(true);
          setDataLoaded(true);
          notification(
            `O email "${email}" não está cadastrado no sistema. Você pode criar um novo solicitante.`,
            'warning'
          );
        } else {
          notification(
            'Ocorreu um erro ao carregar as informações do solicitante.',
            'error'
          );
          console.error('Erro ao buscar informações do solicitante:', error);
        }
      }
    };

    loadRequesterInfo();

    // Adiciona as imagens ao conteúdo da comunicação do cliente, se houver
    if (parsedFiles.length > 0) {
      const imageTags = parsedFiles
        .map(
          (file) =>
            `<img src="${axios.defaults.baseURL}${file}?token=${authToken}" alt="Anexo" style="max-width: 100%; height: auto;" />`
        )
        .join('\n');
      setClientCommunication((prev) => `${prev}\n${imageTags}`);
    }
  }, [email, navigate, state, preticketFiles, preticketMessage, authToken]);

  // Atualiza mesas e opções quando muda a empresa selecionada
  useEffect(() => {
    const applyCompanyChange = async () => {
      if (!selectedCompany) return;
      try {
        const { data: desks } = await axios.get(
          `/servicedesks/customer/${selectedCompany}`
        );
        setServiceDesks(desks || []);
        const d0 = (desks || [])[0];
        if (d0) {
          setSelectedServiceDesk(d0.id);
          setDepartmentOptions(d0.departments || []);
          setPriorityOptions(d0.priorities || []);
          setStageOptions(d0.stages || []);
          setSelectedDepartment(
            d0.departments && d0.departments.length ? d0.departments[0].id : ''
          );
          setSelectedPriority(
            d0.priorities && d0.priorities.length ? d0.priorities[0].id : ''
          );
          setSelectedStage(
            d0.stages && d0.stages.length ? d0.stages[0].id : ''
          );
        } else {
          setSelectedServiceDesk('');
          setDepartmentOptions([]);
          setPriorityOptions([]);
          setStageOptions([]);
          setSelectedDepartment('');
          setSelectedPriority('');
          setSelectedStage('');
        }
      } catch (err) {
        setServiceDesks([]);
      }
    };
    applyCompanyChange();
  }, [selectedCompany]);

  // Quando seleciona uma mesa, atualiza opções relacionadas
  useEffect(() => {
    if (!selectedServiceDesk) return;
    const desk = serviceDesks.find(
      (d) => String(d.id) === String(selectedServiceDesk)
    );
    if (desk) {
      setDepartmentOptions(desk.departments || []);
      setPriorityOptions(desk.priorities || []);
      setStageOptions(desk.stages || []);
      setSelectedDepartment(
        desk.departments && desk.departments.length
          ? desk.departments[0].id
          : ''
      );
      setSelectedPriority(
        desk.priorities && desk.priorities.length ? desk.priorities[0].id : ''
      );
      setSelectedStage(
        desk.stages && desk.stages.length ? desk.stages[0].id : ''
      );
    }
  }, [selectedServiceDesk, serviceDesks]);

  // Carrega detalhes do solicitante para exibir informações completas
  useEffect(() => {
    const loadRequesterDetails = async () => {
      if (!selectedRequesterId) return;
      try {
        const { data } = await axios.get(`/requesters/${selectedRequesterId}`);
        setRequesterDetails(data || null);
      } catch (_) {
        setRequesterDetails(null);
      }
    };
    loadRequesterDetails();
  }, [selectedRequesterId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Ativar o loading

    try {
      const stageIdValue =
        selectedStage || (stageOptions[0] && stageOptions[0].id) || '';
      // Normaliza anexos do pré-ticket para enviar ao backend
      let attachments = [];
      try {
        if (typeof preticketFiles === 'string') {
          attachments = JSON.parse(preticketFiles) || [];
        } else if (Array.isArray(preticketFiles)) {
          attachments = preticketFiles;
        }
        if (!Array.isArray(attachments)) attachments = [];
      } catch (_) {
        attachments = [];
      }
      const ticketData = {
        requesterId: Number(selectedRequesterId),
        company: Number(selectedCompany),
        title,
        assignedTo: Number(userData.id),
        serviceDesk: Number(
          selectedServiceDesk || (serviceDesks[0] && serviceDesks[0].id) || 0
        ),
        status: 'aberto',
        department: Number(selectedDepartment || 0),
        priority: Number(selectedPriority || 0),
        stageid: Number(stageIdValue || 0),
        // Preenche a descrição do ticket com o conteúdo original do preticket
        descricao_ticket: transformPreticketHtml(preticketMessage) || '',
        clientCommunication,
        fromPreticket: true,
        origem: 'Preticket',
        attachments,
      };

      const formData = new FormData();
      formData.append('ticketData', JSON.stringify(ticketData));

      const ticketResponse = await axios.post('/tickets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const ticketId = ticketResponse.data.id;

      await axios.delete(`/pretickets/${preticketId}`);

      navigate('/tickets');
    } catch (error) {
      console.error('Erro ao criar o ticket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded) {
    return <Loading />;
  }

  return (
    <Container fluid className="px-lg-4 px-xl-15">
      <Breadcrumbs
        title="Pré Tickets"
        pages={[{ name: ' Pré Tickets', link: '/pretickets' }]}
      />

      {requesterNotFound && (
        <Card className="mb-3 border-warning">
          <Card.Body className="text-center">
            <h5 className="text-warning mb-3">Solicitante não encontrado</h5>
            <p className="mb-3">
              O email <strong>{email}</strong> não está cadastrado no sistema.
              Você pode criar um novo solicitante com este email.
            </p>
            <Button
              variant="warning"
              onClick={handleOpenRequesterModal}
              className="me-2"
            >
              Criar Novo Solicitante
            </Button>
            <Button variant="secondary" onClick={() => navigate('/pretickets')}>
              Voltar
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Seção de Informações do Cliente e Descrição lado a lado */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Informações do Solicitante</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={(() => {
                    if (!requesterDetails) {
                      return email
                        ? `E-mail: ${email}`
                        : 'Informações não disponíveis';
                    }
                    const lines = [];
                    if (requesterDetails.name)
                      lines.push(`Nome: ${requesterDetails.name}`);
                    lines.push(
                      `E-mail: ${requesterDetails.email || email || '-'}`
                    );
                    if (requesterDetails.telefone)
                      lines.push(`Telefone: ${requesterDetails.telefone}`);
                    if (requesterDetails.celular)
                      lines.push(`Celular: ${requesterDetails.celular}`);
                    if (requesterDetails.ramal)
                      lines.push(`Ramal: ${requesterDetails.ramal}`);
                    return lines.join('\n');
                  })()}
                  disabled
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    fontSize: '14px',
                    lineHeight: '1.5',
                  }}
                />
                <div className="mt-2">
                  <Form.Label>
                    Solicitante / Empresa
                    {allRequesterOptions.length > 1 && (
                      <span
                        className="ms-2 badge bg-warning text-dark"
                        style={{ fontSize: '0.7rem' }}
                      >
                        {allRequesterOptions.length} registros com este e-mail
                      </span>
                    )}
                  </Form.Label>
                  <Form.Select
                    value={`${selectedRequesterId}_${selectedCompany}`}
                    onChange={(e) => {
                      const opt = allRequesterOptions.find(
                        (o) =>
                          `${o.requesterId}_${o.companyId}` === e.target.value
                      );
                      if (opt) {
                        setSelectedRequesterId(opt.requesterId);
                        setSelectedCompany(opt.companyId);
                      }
                    }}
                  >
                    {allRequesterOptions.length === 0 && (
                      <option value="">Nenhum solicitante encontrado</option>
                    )}
                    {allRequesterOptions.map((opt) => (
                      <option
                        key={`${opt.requesterId}_${opt.companyId}`}
                        value={`${opt.requesterId}_${opt.companyId}`}
                      >
                        {opt.requesterName} — {opt.companyName}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="mt-2">
                  <strong>Anexos:</strong>
                  <ul className="mb-0">
                    {(() => {
                      let arr = [];
                      try {
                        const raw = preticketFiles;
                        if (typeof raw === 'string') {
                          arr = JSON.parse(raw);
                        } else if (Array.isArray(raw)) {
                          arr = raw;
                        } else {
                          arr = [];
                        }
                      } catch (_) {
                        arr = [];
                      }
                      return arr.length ? (
                        arr.map((f, i) => {
                          const fileName = String(f).split('/').pop();
                          return (
                            <li key={i}>
                              <a
                                href={`${axios.defaults.baseURL}${f}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {fileName}
                              </a>
                            </li>
                          );
                        })
                      ) : (
                        <li className="text-muted">Sem anexos</li>
                      );
                    })()}
                  </ul>
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Descrição Original do Preticket</Form.Label>
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    minHeight: '200px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: injectTokenInHtml(
                      (preticketMessage || 'Descrição não disponível')
                        .replace(/<p><br><\/p>/g, '<br>')
                        .replace(/<p>/g, '')
                        .replace(/<\/p>/g, '<br>')
                        .replace(/(<br>\s*){2,}/g, '<br><br>')
                        .replace(/^<br>|<br>$/g, '')
                        .replace(
                          /<img([^>]*?)style="([^"]*?)"([^>]*?)>/g,
                          '<img$1style="$2; max-width: 100%; height: auto;"$3>'
                        )
                        .replace(
                          /<img(?![^>]*style)([^>]*?)>/g,
                          '<img$1 style="max-width: 100%; height: auto;">'
                        )
                    ),
                  }}
                />
              </Form.Group>
              <div className="mt-3">
                <Form.Label>Mesa de Serviço</Form.Label>
                <Form.Select
                  value={selectedServiceDesk}
                  onChange={(e) => setSelectedServiceDesk(e.target.value)}
                >
                  {(serviceDesks || []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.displayName || d.name || `Mesa ${d.id}`}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Selecionar Departamento */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Departamento</Form.Label>
                  <Form.Select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="">--Selecione--</option>
                    {departmentOptions.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Selecionar Prioridade */}
              <Col>
                <Form.Group>
                  <Form.Label>Prioridade</Form.Label>
                  <Form.Select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                  >
                    <option value="">--Selecione--</option>
                    {priorityOptions.map((priority) => (
                      <option key={priority.id} value={priority.id}>
                        {priority.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Selecionar Estágio */}
              <Col>
                <Form.Group>
                  <Form.Label>Estágio</Form.Label>
                  <Form.Select
                    value={selectedStage}
                    onChange={(e) => setSelectedStage(e.target.value)}
                  >
                    <option value="">--Selecione--</option>
                    {stageOptions.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="mt-2"
                variant="success"
              >
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Registrar Ticket'
                )}
              </Button>
              <Button
                type="button"
                className="mt-2"
                variant="outline-danger"
                onClick={async () => {
                  if (!preticketId) {
                    navigate('/pretickets');
                    return;
                  }
                  const ok = window.confirm(
                    'Deseja descartar este pré-ticket?'
                  );
                  if (!ok) return;
                  try {
                    setIsSubmitting(true);
                    await axios.delete(`/pretickets/${preticketId}`);
                    navigate('/pretickets');
                  } catch (err) {
                    console.error('Erro ao descartar pré-ticket:', err);
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                Descartar Pré-ticket
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <RequesterModal
        show={showRequesterModal}
        onHide={handleCloseRequesterModal}
        onRequesterCreated={handleRequesterCreated}
        prefilledEmail={email}
      />
    </Container>
  );
}
